import React, { useState } from 'react';
import { 
  FiUser, FiMail, FiMapPin, FiLayout, FiImage, 
  FiPlus, FiTrash2, FiBriefcase, FiGlobe, FiPhone, FiLayers, FiGrid, FiMaximize, FiRefreshCw, FiType 
} from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { profiles } from '../data/profiles';
import { getRandomTheme } from '../utils/themes';

// Reusable Input Card Component
const InputCard = ({ title, icon, children, className = "" }) => (
  <div className={`bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden backdrop-blur-sm ${className}`}>
    <div className="px-4 py-3 bg-zinc-900 border-b border-zinc-800 flex items-center gap-2">
      <SafeIcon icon={icon} className="text-blue-500" />
      <h3 className="text-sm font-bold text-zinc-200 uppercase tracking-wide">{title}</h3>
    </div>
    <div className="p-4 space-y-4">
      {children}
    </div>
  </div>
);

const InputField = ({ label, value, onChange, placeholder, type = "text", as = "input" }) => (
    <div className="space-y-1">
        {label && <label className="text-xs font-medium text-zinc-500 uppercase">{label}</label>}
        {as === 'textarea' ? (
            <textarea 
                value={value} onChange={onChange} placeholder={placeholder} 
                rows={4}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-lg p-3 text-sm text-white focus:border-blue-500 outline-none transition-colors resize-none"
            />
        ) : (
            <input 
                type={type} value={value} onChange={onChange} placeholder={placeholder} 
                className="w-full bg-zinc-950 border border-zinc-800 rounded-lg p-2.5 text-sm text-white focus:border-blue-500 outline-none transition-colors"
            />
        )}
    </div>
);

function EditorPanel({ bioData, setBioData, fileInputRef }) {
  const [activeTab, setActiveTab] = useState('content'); // 'content' or 'appearance'

  const handleInputChange = (field, value) => {
      setBioData(prev => ({ ...prev, [field]: value }));
  };
  
  const updateList = (key, idx, field, val) => {
    setBioData(prev => {
        const newList = prev[key].map((item, i) => {
            if (i === idx) {
                return { ...item, [field]: val };
            }
            return item;
        });
        return { ...prev, [key]: newList };
    });
  };
  
  const addListItem = (key, item) => {
      setBioData(prev => ({ ...prev, [key]: [...prev[key], item] }));
  };

  const removeListItem = (key, idx) => {
      setBioData(prev => ({ ...prev, [key]: prev[key].filter((_, i) => i !== idx) }));
  };

  const handleThemeChange = (key, val) => {
      setBioData(prev => ({ ...prev, colors: { ...prev.colors, [key]: val } }));
  };

  const handleCardStyleChange = (key, val) => {
      setBioData(prev => ({ ...prev, cardStyle: { ...prev.cardStyle, [key]: val } }));
  };

  const loadProfile = (profileId) => {
      const selected = profiles.find(p => p.id === profileId);
      if (selected) {
          if (window.confirm(`Load profile for ${selected.name}? This will overwrite current data.`)) {
             setBioData(prev => ({
                 ...prev,
                 ...selected,
                 socials: { ...prev.socials, ...selected.socials },
             }));
          }
      }
  };
  
  const generateRandomLook = () => {
      const theme = getRandomTheme();
      setBioData(prev => ({
          ...prev,
          colors: theme.colors,
          cardStyle: theme.cardStyle,
          fontFamily: theme.fontFamily,
          fontEffect: theme.fontEffect
      }));
  };

  const fonts = [
    'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins', 
    'Playfair Display', 'Merriweather', 'Nunito', 'Raleway', 'Oswald',
    'Pacifico', 'Dancing Script'
  ];

  return (
    <div className="h-full flex flex-col gap-6">
       {/* Tabs */}
       <div className="flex bg-zinc-900 p-1 rounded-lg border border-zinc-800 sticky top-0 z-20 shadow-xl">
          <button 
            onClick={() => setActiveTab('content')}
            className={`flex-1 py-2 rounded-md text-sm font-bold transition-all ${activeTab === 'content' ? 'bg-zinc-800 text-white shadow-lg' : 'text-zinc-500 hover:text-zinc-300'}`}
          >
            Content Editor
          </button>
          <button 
            onClick={() => setActiveTab('appearance')}
            className={`flex-1 py-2 rounded-md text-sm font-bold transition-all ${activeTab === 'appearance' ? 'bg-zinc-800 text-white shadow-lg' : 'text-zinc-500 hover:text-zinc-300'}`}
          >
            Appearance
          </button>
       </div>

       <div className="editor-scroll overflow-y-auto h-full pb-20 pr-1">
       {activeTab === 'content' ? (
          <div className="grid grid-cols-1 gap-6">
             
             {/* Profile Loader */}
             <div className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 border border-blue-500/30 rounded-xl p-4">
                <label className="text-xs font-bold text-blue-400 uppercase mb-2 flex items-center gap-2">
                    <FiUser /> Quick Load Profile
                </label>
                <select 
                    onChange={(e) => loadProfile(e.target.value)}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg p-2 text-sm text-white outline-none focus:border-blue-500 transition-colors cursor-pointer"
                    defaultValue=""
                >
                    <option value="" disabled>Select a profile...</option>
                    {profiles.map(p => (
                        <option key={p.id} value={p.id}>{p.name} - {p.title}</option>
                    ))}
                </select>
             </div>

             {/* LEFT COLUMN CONTENT */}
             <div className="space-y-6">
                {/* Avatar */}
                <InputCard title="Profile Image" icon={FiImage}>
                    <div className="flex items-center gap-4">
                        <div onClick={() => fileInputRef.current?.click()} className="w-20 h-20 rounded-full bg-zinc-900 border-2 border-dashed border-zinc-700 hover:border-blue-500 cursor-pointer overflow-hidden flex items-center justify-center relative group transition-colors">
                            {bioData.avatar ? <img src={bioData.avatar} className="w-full h-full object-cover" alt="Avatar Preview" /> : <FiImage className="text-zinc-600 text-xl" />}
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center text-xs font-bold text-white">Change</div>
                        </div>
                        <div className="text-sm text-zinc-400">
                            <button onClick={() => fileInputRef.current?.click()} className="text-blue-400 hover:underline font-medium">Upload Image</button>
                            <p className="text-xs mt-1">Recommended: Square JPG/PNG</p>
                        </div>
                    </div>
                </InputCard>

                {/* Contact */}
                <InputCard title="Contact Details" icon={FiPhone}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <InputField label="Phone" value={bioData.phone} onChange={(e) => handleInputChange('phone', e.target.value)} />
                        <InputField label="Email" value={bioData.email} onChange={(e) => handleInputChange('email', e.target.value)} />
                    </div>
                    <InputField label="Location" value={bioData.location} onChange={(e) => handleInputChange('location', e.target.value)} />
                    <InputField label="Website" value={bioData.website} onChange={(e) => handleInputChange('website', e.target.value)} />
                    
                    <div className="pt-2 border-t border-zinc-800 mt-2">
                        <label className="text-xs font-medium text-zinc-500 uppercase mb-2 block">Social Links</label>
                        <div className="space-y-2">
                            {['linkedin', 'x', 'github', 'youtube'].map(platform => (
                                <div key={platform} className="relative">
                                    <div className="absolute left-3 top-2.5 text-zinc-500 text-xs capitalize font-bold w-16">{platform}</div>
                                    <input 
                                        type="text" 
                                        value={bioData.socials?.[platform] || ''} 
                                        onChange={(e) => setBioData(prev => ({...prev, socials: {...prev.socials, [platform]: e.target.value}}))}
                                        className="w-full bg-zinc-950 border border-zinc-800 rounded-lg p-2 pl-20 text-sm text-white focus:border-blue-500 outline-none"
                                        placeholder="Username or URL"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </InputCard>

                {/* Skills */}
                <InputCard title="Skills" icon={FiGrid}>
                    <InputField 
                        as="textarea" 
                        placeholder="UI/UX, React, JavaScript, Project Management..."
                        value={bioData.skills.join(', ')} 
                        onChange={(e) => handleInputChange('skills', e.target.value.split(',').map(s => s.trim()))} 
                    />
                    <p className="text-xs text-zinc-500">Separate skills with commas</p>
                </InputCard>
                
                 {/* Languages */}
                 <InputCard title="Languages" icon={FiGlobe}>
                    <InputField 
                        placeholder="English, Spanish, French..."
                        value={bioData.languages.join(', ')} 
                        onChange={(e) => handleInputChange('languages', e.target.value.split(',').map(s => s.trim()))} 
                    />
                </InputCard>
             </div>

             {/* RIGHT COLUMN CONTENT */}
             <div className="space-y-6">
                {/* Header Info */}
                <InputCard title="Header Info" icon={FiUser}>
                    <InputField label="Full Name" value={bioData.name} onChange={(e) => handleInputChange('name', e.target.value)} />
                    <InputField label="Job Title" value={bioData.title} onChange={(e) => handleInputChange('title', e.target.value)} />
                    <InputField as="textarea" label="Profile Summary" value={bioData.about} onChange={(e) => handleInputChange('about', e.target.value)} />
                </InputCard>

                {/* Experience */}
                <InputCard title="Experience" icon={FiBriefcase}>
                    {bioData.experience.map((exp, idx) => (
                        <div key={idx} className="p-3 bg-zinc-950 border border-zinc-800 rounded-lg space-y-2 relative group">
                            <button onClick={() => removeListItem('experience', idx)} className="absolute top-2 right-2 text-zinc-600 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"><FiTrash2 /></button>
                            <div className="grid grid-cols-2 gap-2">
                                <input placeholder="Years" value={exp.year} onChange={(e) => updateList('experience', idx, 'year', e.target.value)} className="bg-transparent border-b border-zinc-800 p-1 text-sm text-white w-full outline-none focus:border-blue-500" />
                                <input placeholder="Company" value={exp.company} onChange={(e) => updateList('experience', idx, 'company', e.target.value)} className="bg-transparent border-b border-zinc-800 p-1 text-sm text-white w-full outline-none focus:border-blue-500" />
                            </div>
                            <input placeholder="Role" value={exp.role} onChange={(e) => updateList('experience', idx, 'role', e.target.value)} className="bg-transparent border-b border-zinc-800 p-1 text-sm font-bold text-blue-400 w-full outline-none" />
                            <textarea placeholder="Description" value={exp.desc} onChange={(e) => updateList('experience', idx, 'desc', e.target.value)} className="bg-transparent w-full text-xs text-zinc-400 outline-none resize-none" rows={2} />
                        </div>
                    ))}
                    <button onClick={() => addListItem('experience', { year: '', company: '', role: '', desc: '' })} className="w-full py-2 border border-dashed border-zinc-700 rounded-lg text-zinc-500 hover:text-white text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-zinc-800 transition-colors">
                        <FiPlus /> Add Position
                    </button>
                </InputCard>

                 {/* Education */}
                 <InputCard title="Education" icon={FiLayers}>
                    {bioData.education.map((edu, idx) => (
                        <div key={idx} className="p-3 bg-zinc-950 border border-zinc-800 rounded-lg space-y-2 relative group">
                            <button onClick={() => removeListItem('education', idx)} className="absolute top-2 right-2 text-zinc-600 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"><FiTrash2 /></button>
                            <div className="grid grid-cols-3 gap-2">
                                <input placeholder="Year" value={edu.year} onChange={(e) => updateList('education', idx, 'year', e.target.value)} className="col-span-1 bg-transparent border-b border-zinc-800 p-1 text-sm text-white w-full outline-none focus:border-blue-500" />
                                <input placeholder="Degree" value={edu.degree} onChange={(e) => updateList('education', idx, 'degree', e.target.value)} className="col-span-2 bg-transparent border-b border-zinc-800 p-1 text-sm text-white w-full outline-none focus:border-blue-500" />
                            </div>
                            <input placeholder="School/University" value={edu.school} onChange={(e) => updateList('education', idx, 'school', e.target.value)} className="bg-transparent w-full text-xs text-zinc-400 outline-none" />
                        </div>
                    ))}
                    <button onClick={() => addListItem('education', { year: '', degree: '', school: '' })} className="w-full py-2 border border-dashed border-zinc-700 rounded-lg text-zinc-500 hover:text-white text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-zinc-800 transition-colors">
                        <FiPlus /> Add Education
                    </button>
                </InputCard>

                {/* References */}
                <InputCard title="References" icon={FiUser}>
                    {bioData.references.map((ref, idx) => (
                        <div key={idx} className="p-3 bg-zinc-950 border border-zinc-800 rounded-lg space-y-2 relative group">
                            <button onClick={() => removeListItem('references', idx)} className="absolute top-2 right-2 text-zinc-600 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"><FiTrash2 /></button>
                            <input placeholder="Name" value={ref.name} onChange={(e) => updateList('references', idx, 'name', e.target.value)} className="bg-transparent border-b border-zinc-800 p-1 text-sm font-bold text-white w-full outline-none" />
                            <input placeholder="Role, Company" value={`${ref.role}, ${ref.company}`} onChange={(e) => {
                                const [r, c] = e.target.value.split(',');
                                updateList('references', idx, 'role', r || '');
                                updateList('references', idx, 'company', c || '');
                            }} className="bg-transparent border-b border-zinc-800 p-1 text-xs text-zinc-400 w-full outline-none" />
                            <input placeholder="Contact Info" value={ref.contact} onChange={(e) => updateList('references', idx, 'contact', e.target.value)} className="bg-transparent w-full text-xs text-zinc-500 outline-none" />
                        </div>
                    ))}
                    <button onClick={() => addListItem('references', { name: '', role: '', company: '', contact: '' })} className="w-full py-2 border border-dashed border-zinc-700 rounded-lg text-zinc-500 hover:text-white text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-zinc-800 transition-colors">
                        <FiPlus /> Add Reference
                    </button>
                </InputCard>
             </div>
          </div>
       ) : (
          /* APPEARANCE TAB */
          <div className="space-y-6">
             {/* Random Generator */}
             <div className="bg-gradient-to-r from-purple-900/40 to-pink-900/40 border border-purple-500/30 rounded-xl p-6 text-center">
                 <h3 className="text-white font-bold mb-2">Feeling Lucky?</h3>
                 <p className="text-zinc-400 text-xs mb-4">Instantly generate a unique, professional color theme and style.</p>
                 <button 
                    onClick={generateRandomLook}
                    className="w-full py-3 bg-white text-black font-bold rounded-lg shadow-[0_0_20px_rgba(255,255,255,0.15)] hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2"
                 >
                    <SafeIcon icon={FiRefreshCw} className="text-lg" />
                    Generate Random Theme
                 </button>
             </div>

             <InputCard title="Typography" icon={FiType}>
                <div className="space-y-4">
                    <div>
                        <label className="text-xs font-medium text-zinc-500 uppercase mb-2 block">Font Family</label>
                        <select 
                            value={bioData.fontFamily || 'Roboto'}
                            onChange={(e) => setBioData(prev => ({ ...prev, fontFamily: e.target.value }))}
                            className="w-full bg-zinc-950 border border-zinc-800 rounded-lg p-2 text-sm text-white outline-none"
                        >
                            {fonts.map(font => (
                                <option key={font} value={font} style={{ fontFamily: font }}>{font}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs font-medium text-zinc-500 uppercase mb-2 block">Font Effect</label>
                        <select 
                            value={bioData.fontEffect || 'none'}
                            onChange={(e) => setBioData(prev => ({ ...prev, fontEffect: e.target.value }))}
                            className="w-full bg-zinc-950 border border-zinc-800 rounded-lg p-2 text-sm text-white outline-none capitalize"
                        >
                             <option value="none">None</option>
                             <option value="metallic-gold">Metallic Gold</option>
                             <option value="metallic-silver">Metallic Silver</option>
                             <option value="metallic-bronze">Metallic Bronze</option>
                             <option value="metallic-steel">Metallic Steel</option>
                        </select>
                    </div>
                </div>
             </InputCard>

             <InputCard title="Global Colors" icon={FiLayout}>
                <div className="grid grid-cols-1 gap-4">
                   {[
                      { l: 'Page Background', k: 'background' },
                      { l: 'Card Background', k: 'cardBackground' },
                      { l: 'Card Border', k: 'cardBorder' },
                      { l: 'Primary Text', k: 'text' },
                      { l: 'Accent Color', k: 'accent' }
                   ].map(c => (
                      <div key={c.k} className="flex items-center justify-between">
                         <span className="text-sm text-zinc-400">{c.l}</span>
                         <div className="flex items-center gap-2">
                            <span className="text-xs font-mono text-zinc-600 uppercase truncate max-w-[80px]">{bioData.colors[c.k]}</span>
                            <div className="relative w-8 h-8 rounded border border-zinc-700 overflow-hidden">
                                <input 
                                    type="color" 
                                    value={bioData.colors[c.k]?.startsWith('#') ? bioData.colors[c.k] : '#000000'} // Fallback for gradients
                                    onChange={(e) => handleThemeChange(c.k, e.target.value)} 
                                    className="absolute -top-2 -left-2 w-12 h-12 cursor-pointer p-0 border-0"
                                />
                                {/* Show gradient preview if value is not a simple hex */}
                                {!bioData.colors[c.k]?.startsWith('#') && (
                                    <div className="absolute inset-0 pointer-events-none" style={{ background: bioData.colors[c.k] }}></div>
                                )}
                            </div>
                         </div>
                      </div>
                   ))}
                </div>
             </InputCard>

             <InputCard title="Card Styling" icon={FiMaximize}>
                <div className="space-y-4">
                    <div>
                        <div className="flex justify-between mb-2">
                            <label className="text-xs font-medium text-zinc-500 uppercase">Border Radius</label>
                            <span className="text-xs text-zinc-400">{bioData.cardStyle.borderRadius}</span>
                        </div>
                        <input 
                            type="range" min="0" max="32" 
                            value={parseInt(bioData.cardStyle.borderRadius)} 
                            onChange={(e) => handleCardStyleChange('borderRadius', `${e.target.value}px`)}
                            className="w-full accent-blue-500 h-1 bg-zinc-800 rounded-lg appearance-none cursor-pointer"
                        />
                    </div>
                    <div>
                        <div className="flex justify-between mb-2">
                            <label className="text-xs font-medium text-zinc-500 uppercase">Border Width</label>
                            <span className="text-xs text-zinc-400">{bioData.cardStyle.borderWidth}</span>
                        </div>
                        <input 
                            type="range" min="0" max="5" 
                            value={parseInt(bioData.cardStyle.borderWidth)} 
                            onChange={(e) => handleCardStyleChange('borderWidth', `${e.target.value}px`)}
                            className="w-full accent-blue-500 h-1 bg-zinc-800 rounded-lg appearance-none cursor-pointer"
                        />
                    </div>
                    <div>
                        <label className="text-xs font-medium text-zinc-500 uppercase mb-2 block">Shadow Intensity</label>
                        <select 
                            value={bioData.cardStyle.shadow}
                            onChange={(e) => handleCardStyleChange('shadow', e.target.value)}
                            className="w-full bg-zinc-950 border border-zinc-800 rounded-lg p-2 text-sm text-white outline-none"
                        >
                            <option value="none">None</option>
                            <option value="0 4px 6px -1px rgba(0, 0, 0, 0.1)">Soft</option>
                            <option value="0 10px 15px -3px rgba(0, 0, 0, 0.3)">Medium</option>
                            <option value="0 20px 25px -5px rgba(0, 0, 0, 0.5)">Strong</option>
                            <option value="0 25px 50px -12px rgba(0, 0, 0, 0.7)">Heavy</option>
                        </select>
                    </div>
                     <div>
                        <label className="text-xs font-medium text-zinc-500 uppercase mb-2 block">Text Alignment</label>
                        <div className="flex bg-zinc-950 rounded-lg border border-zinc-800 p-1">
                             {['left', 'center', 'right'].map(align => (
                                 <button 
                                    key={align}
                                    onClick={() => handleCardStyleChange('textAlign', align)}
                                    className={`flex-1 py-1 rounded text-xs capitalize ${bioData.cardStyle.textAlign === align ? 'bg-zinc-800 text-white' : 'text-zinc-500'}`}
                                 >
                                    {align}
                                 </button>
                             ))}
                        </div>
                    </div>
                </div>
             </InputCard>
          </div>
       )}
       </div>
    </div>
  );
}

export default EditorPanel;